import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import { toUploadUrl } from '../config/api';

const About = () => {
  const statsRef = useRef(null);
  const [isStatsVisible, setIsStatsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsStatsVisible(true);
        }
      },
      { threshold: 0.3 }
    );

    if (statsRef.current) {
      observer.observe(statsRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const teamMembers = [
    {
      name: 'Er. Surya Pratap Singh',
      position: 'Founder & Managing Director',
      qualification: 'NIT, 2015 | 10+ Years Experience',
      image: toUploadUrl('surya.png'),
      expertise: ['Strategic Planning', 'Business Development', 'Customer Relations'],
      description: 'Visionary leader with a passion for creating unforgettable travel experiences.',
      social: { linkedin: '#', twitter: '#', email: 'surya@redgotrips.com' }
    },
    {
      name: 'Prasant Kumar',
      position: 'Head of Travel Operations',
      qualification: 'Tourism Management | 12+ Years',
      image: toUploadUrl('prasant.jpeg'),
      expertise: ['Itinerary Planning', 'Vendor Management', 'Quality Control'],
      description: 'Kashmir travel specialist with deep knowledge of hidden gems.',
      social: { linkedin: '#', twitter: '#', email: 'priya@redgotrips.com' }
    },
    {
      name: 'Anuj Kumar Singh',
      position: 'Customer Experience Director',
      qualification: 'Hospitality Management | 8+ Years',
      image: toUploadUrl('anuj.png'),
      expertise: ['Client Relations', 'Service Excellence', 'Problem Resolution'],
      description: 'Dedicated to ensuring every traveler has a seamless and memorable journey.',
      social: { linkedin: '#', twitter: '#', email: 'rahul@redgotrips.com' }
    }
  ];

  const milestones = [
    { year: '2019', title: 'The Vision Begins', description: 'Founded with a vision to provide authentic Kashmir travel experiences.', icon: 'fas fa-lightbulb', color: '#FF6B35' },
    { year: '2020', title: 'First 100 Customers', description: 'Achieved milestone of 100+ happy travelers.', icon: 'fas fa-users', color: '#4361EE' },
    { year: '2021', title: 'Expansion to Gulmarg', description: 'Opened new office in Gulmarg.', icon: 'fas fa-map-marked-alt', color: '#7209B7' },
    { year: '2022', title: 'Official Incorporation', description: 'Incorporated as RedGoTrip Pvt. Ltd.', icon: 'fas fa-building', color: '#F72585' },
    { year: '2023', title: '500+ Happy Travelers', description: 'Crossed 500+ satisfied customers.', icon: 'fas fa-star', color: '#4CC9F0' },
    { year: '2024', title: 'Pan-India Presence', description: 'Expanded operations across North India.', icon: 'fas fa-flag', color: '#06D6A0' },
    { year: '2025', title: 'Industry Recognition', description: 'Received "Best Emerging Travel Company" award.', icon: 'fas fa-award', color: '#FFB703' },
    { year: '2026', title: '1000+ Milestone', description: 'Proudly served over 1000+ travelers.', icon: 'fas fa-chart-line', color: '#E63946' }
  ];

  const values = [
    { icon: 'fas fa-heart', title: 'Customer First', description: 'Every decision centers around your satisfaction.', stats: '98% Satisfaction Rate' },
    { icon: 'fas fa-shield-alt', title: 'Trust & Transparency', description: 'No hidden costs, clear communication.', stats: '100% Transparent' },
    { icon: 'fas fa-star', title: 'Quality Assurance', description: 'Meticulously curated packages.', stats: '4.8/5 Rating' },
    { icon: 'fas fa-hand-holding-heart', title: 'Personalized Service', description: 'Tailor-made itineraries.', stats: '500+ Custom Tours' },
    { icon: 'fas fa-clock', title: '24/7 Support', description: 'Round-the-clock assistance.', stats: 'Always Available' },
    { icon: 'fas fa-leaf', title: 'Responsible Tourism', description: 'Sustainable travel practices.', stats: 'Eco-Certified' }
  ];

  const achievements = [
    { value: '1000+', label: 'Happy Travelers', icon: 'fas fa-smile' },
    { value: '500+', label: 'Customized Tours', icon: 'fas fa-map-marked-alt' },
    { value: '50+', label: 'Destinations', icon: 'fas fa-location-dot' },
    { value: '4.8', label: 'Customer Rating', icon: 'fas fa-star' },
    { value: '8+', label: 'Years', icon: 'fas fa-calendar-alt' },
    { value: '24/7', label: 'Support', icon: 'fas fa-headset' }
  ];

  const testimonials = [
    {
      name: 'Rahul Mehta',
      location: 'Mumbai',
      comment: 'RedGoTrip made our Kashmir honeymoon absolutely magical. The houseboat stay was unforgettable!',
      rating: 5,
      image: toUploadUrl('rahul.png')
    },
    {
      name: 'Priya Sharma',
      location: 'Delhi',
      comment: 'Our family trip to Gulmarg was seamless. Highly recommended for family tours!',
      rating: 5,
      image: toUploadUrl('priya.png')
    },
    {
      name: 'Amit Patel',
      location: 'Ahmedabad',
      comment: 'Professional team, excellent service, and great value for money.',
      rating: 5,
      image: toUploadUrl('amit.png')
    }
  ];

  return (
    <div className="about-page">
      <SEO
        title="About RedGoTrip | Trusted Travel Partner"
        description="Learn about RedGoTrip, our travel story, customer-first values, milestones and the team behind curated holiday experiences."
        canonicalPath="/about"
        keywords="about RedGoTrip, travel company, trusted travel partner, holiday experts"
      />
      {/* Modern Hero Section */}

      {/* Story Section with Cards */}
      <section className="story-section">
        <div className="container">
          <div className="section-header">
            <span className="section-tag">Our Story</span>
            <h2>A Journey of <span className="highlight">Passion & Excellence</span></h2>
            <p>From a vision to India's most trusted travel company</p>
          </div>

          <div className="story-grid">
            <div className="story-card">
              <div className="story-icon">
                <i className="fas fa-rocket"></i>
              </div>
              <h3>How It Started</h3>
              <p>Founded in 2019 by Er. Surya Pratap Singh, an NIT alumnus with a deep love for Kashmir.</p>
            </div>

            <div className="story-card">
              <div className="story-icon">
                <i className="fas fa-chart-line"></i>
              </div>
              <h3>Growth & Success</h3>
              <p>From one person to a team of passionate experts serving 1000+ happy travelers.</p>
            </div>

            <div className="story-card">
              <div className="story-icon">
                <i className="fas fa-trophy"></i>
              </div>
              <h3>Awards & Recognition</h3>
              <p>Received "Best Emerging Travel Company" award from Travel India Association.</p>
            </div>

            <div className="story-card">
              <div className="story-icon">
                <i className="fas fa-globe"></i>
              </div>
              <h3>Pan-India Presence</h3>
              <p>Expanded operations to cover all major destinations across North India.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="stats-section" ref={statsRef}>
        <div className="container">
          <div className="stats-grid">
            {achievements.map((stat, index) => (
              <div key={index} className="stat-card">
                <div className="stat-icon">
                  <i className={stat.icon}></i>
                </div>
                <div className="stat-content">
                  <div className="stat-value">{isStatsVisible ? stat.value : '0'}</div>
                  <div className="stat-label">{stat.label}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="values-section">
        <div className="container">
          <div className="section-header">
            <span className="section-tag">What Drives Us</span>
            <h2>Our Core <span className="highlight">Values</span></h2>
            <p>Principles that guide every interaction and decision</p>
          </div>
          <div className="values-grid">
            {values.map((value, index) => (
              <div key={index} className="value-card">
                <div className="value-icon">
                  <i className={value.icon}></i>
                </div>
                <h3>{value.title}</h3>
                <p className="value-description">{value.description}</p>
                <div className="value-stats">{value.stats}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline Journey */}
      <section className="timeline-section">
        <div className="container">
          <div className="section-header">
            <span className="section-tag">Our Journey</span>
            <h2>Milestones That <span className="highlight">Define Us</span></h2>
            <p>Every step of our growth story</p>
          </div>
          <div className="timeline">
            {milestones.map((milestone, index) => (
              <div key={index} className={`timeline-item ${index % 2 === 0 ? 'left' : 'right'}`}>
                <div className="timeline-marker" style={{ background: milestone.color }}>
                  <i className={milestone.icon}></i>
                </div>
                <div className="timeline-content">
                  <span className="timeline-year">{milestone.year}</span>
                  <h3>{milestone.title}</h3>
                  <p>{milestone.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="team-section">
        <div className="container">
          <div className="section-header">
            <span className="section-tag">Leadership</span>
            <h2>Meet The <span className="highlight">Team</span></h2>
            <p>Passionate experts dedicated to your travel dreams</p>
          </div>
          <div className="team-grid">
            {teamMembers.map((member, index) => (
              <div key={index} className="team-card">
                <div className="member-image">
                  <img src={member.image} alt={member.name} />
                  <div className="member-social">
                    <a href={member.social.linkedin} className="social-link">
                      <i className="fab fa-linkedin-in"></i>
                    </a>
                    <a href={member.social.twitter} className="social-link">
                      <i className="fab fa-twitter"></i>
                    </a>
                    <a href={`mailto:${member.social.email}`} className="social-link">
                      <i className="fas fa-envelope"></i>
                    </a>
                  </div>
                </div>
                <div className="member-info">
                  <h3>{member.name}</h3>
                  <p className="member-position">{member.position}</p>
                  <p className="member-qualification">{member.qualification}</p>
                  <p className="member-description">{member.description}</p>
                  <div className="member-expertise">
                    {member.expertise.map((skill, idx) => (
                      <span key={idx} className="expertise-tag">{skill}</span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="testimonials-section">
        <div className="container">
          <div className="section-header">
            <span className="section-tag">Testimonials</span>
            <h2>What Our <span className="highlight">Travelers Say</span></h2>
            <p>Real experiences from happy customers</p>
          </div>
          <div className="testimonials-grid">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="testimonial-card">
                <i className="fas fa-quote-left quote-icon"></i>
                <p className="testimonial-text">"{testimonial.comment}"</p>
                <div className="testimonial-footer">
                  <img src={testimonial.image} alt={testimonial.name} />
                  <div>
                    <h4>{testimonial.name}</h4>
                    <p>{testimonial.location}</p>
                  </div>
                </div>
                <div className="testimonial-rating">
                  {[...Array(5)].map((_, i) => (
                    <i key={i} className={`fas fa-star ${i < testimonial.rating ? 'filled' : ''}`}></i>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="container">
          <div className="cta-content">
            <h2>Ready to Experience Kashmir?</h2>
            <p>Let us craft the perfect itinerary for your dream vacation</p>
            <div className="cta-buttons">
              <Link to="/packages" className="cta-btn primary">
                Explore Packages
                <i className="fas fa-arrow-right"></i>
              </Link>
              <Link to="/contact" className="cta-btn secondary">
                <i className="fas fa-phone-alt"></i>
                Contact Us
              </Link>
            </div>
          </div>
        </div>
      </section>

      <style>{`
        .about-page {
          overflow-x: hidden;
          background: #ffffff;
        }

        .container {
          max-width: 1400px;
          margin: 0 auto;
          padding: 0 2rem;
        }

        .section-header {
          text-align: center;
          margin-bottom: 4rem;
        }

        .section-tag {
          display: inline-block;
          background: linear-gradient(135deg, #FF6B3515 0%, #4361EE15 100%);
          color: #FF6B35;
          padding: 0.5rem 1.5rem;
          border-radius: 50px;
          font-size: 14px;
          font-weight: 600;
          margin-bottom: 1rem;
          text-transform: uppercase;
          letter-spacing: 1px;
        }

        .section-header h2 {
          font-size: 42px;
          color: #1a202c;
          margin-bottom: 1rem;
          font-weight: 800;
        }

        .highlight {
          color: #FF6B35;
          position: relative;
        }

        .highlight::after {
          content: '';
          position: absolute;
          bottom: 5px;
          left: 0;
          width: 100%;
          height: 8px;
          background: rgba(255,107,53,0.2);
          z-index: -1;
        }

        .section-header p {
          color: #718096;
          font-size: 18px;
        }

        /* Hero Section */
        .about-hero {
          position: relative;
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          overflow: hidden;
        }

        .hero-particles {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-image: 
            radial-gradient(circle at 20% 30%, rgba(255,255,255,0.1) 0%, transparent 20%),
            radial-gradient(circle at 80% 70%, rgba(255,255,255,0.1) 0%, transparent 20%),
            radial-gradient(circle at 40% 80%, rgba(255,255,255,0.1) 0%, transparent 30%),
            radial-gradient(circle at 70% 20%, rgba(255,255,255,0.1) 0%, transparent 30%);
          animation: particleFloat 20s ease infinite;
        }

        @keyframes particleFloat {
          0%, 100% { transform: translate(0, 0); }
          25% { transform: translate(2%, 2%); }
          75% { transform: translate(-2%, -2%); }
        }

        .hero-content {
          position: relative;
          z-index: 2;
          text-align: center;
          color: white;
          max-width: 900px;
          padding: 0 2rem;
        }

        .hero-subtitle {
          display: inline-block;
          font-size: 18px;
          text-transform: uppercase;
          letter-spacing: 3px;
          margin-bottom: 1.5rem;
          opacity: 0.9;
          animation: fadeInUp 1s ease;
        }

        .hero-title {
          margin-bottom: 2rem;
          animation: fadeInUp 1s ease 0.2s both;
        }

        .title-line {
          display: block;
          font-size: 72px;
          font-weight: 800;
          line-height: 1.1;
        }

        .title-line.highlight {
          color: #FFD700;
          text-shadow: 0 0 30px rgba(255,215,0,0.3);
        }

        .hero-description {
          font-size: 20px;
          line-height: 1.8;
          margin-bottom: 3rem;
          opacity: 0.95;
          max-width: 700px;
          margin-left: auto;
          margin-right: auto;
          animation: fadeInUp 1s ease 0.4s both;
        }

        .hero-stats {
          display: flex;
          justify-content: center;
          gap: 4rem;
          margin-bottom: 3rem;
          animation: fadeInUp 1s ease 0.6s both;
        }

        .hero-stat {
          text-align: center;
        }

        .stat-number {
          display: block;
          font-size: 36px;
          font-weight: 800;
          margin-bottom: 0.5rem;
        }

        .stat-label {
          font-size: 14px;
          opacity: 0.8;
          text-transform: uppercase;
          letter-spacing: 1px;
        }

        .hero-cta {
          display: flex;
          gap: 1rem;
          justify-content: center;
          animation: fadeInUp 1s ease 0.8s both;
        }

        .cta-btn {
          padding: 1rem 2.5rem;
          border-radius: 50px;
          font-weight: 600;
          text-decoration: none;
          transition: all 0.3s;
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
        }

        .cta-btn.primary {
          background: white;
          color: #667eea;
        }

        .cta-btn.primary:hover {
          transform: translateY(-2px);
          box-shadow: 0 10px 25px rgba(0,0,0,0.2);
        }

        .cta-btn.secondary {
          background: transparent;
          color: white;
          border: 2px solid white;
        }

        .cta-btn.secondary:hover {
          background: white;
          color: #667eea;
        }

        .hero-wave {
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
        }

        .hero-wave svg {
          width: 100%;
          height: auto;
          display: block;
          fill: white;
        }

        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(30px); }
          to { opacity: 1; transform: translateY(0); }
        }

        /* Story Cards */
        .story-section {
          padding: 6rem 0;
          background: white;
        }

        .story-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 2rem;
        }

        .story-card {
          background: white;
          padding: 2.5rem 2rem;
          border-radius: 20px;
          text-align: center;
          box-shadow: 0 10px 30px rgba(0,0,0,0.05);
          transition: all 0.3s;
          border: 1px solid #f0f0f0;
        }

        .story-card:hover {
          transform: translateY(-10px);
          box-shadow: 0 20px 40px rgba(255,107,53,0.1);
          border-color: #FF6B35;
        }

        .story-icon {
          width: 80px;
          height: 80px;
          background: linear-gradient(135deg, #FF6B35, #FF8A5C);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 1.5rem;
          font-size: 32px;
          color: white;
          transition: all 0.3s;
        }

        .story-card:hover .story-icon {
          transform: rotateY(180deg);
        }

        .story-card h3 {
          font-size: 20px;
          color: #1a202c;
          margin-bottom: 1rem;
        }

        .story-card p {
          color: #718096;
          line-height: 1.6;
        }

        /* Stats Section */
        .stats-section {
          padding: 4rem 0;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        }

        .stats-grid {
          display: grid;
          grid-template-columns: repeat(6, 1fr);
          gap: 1.5rem;
        }

        .stat-card {
          background: rgba(255,255,255,0.1);
          backdrop-filter: blur(10px);
          padding: 1.5rem;
          border-radius: 15px;
          text-align: center;
          border: 1px solid rgba(255,255,255,0.2);
          transition: all 0.3s;
        }

        .stat-card:hover {
          transform: translateY(-5px);
          background: rgba(255,255,255,0.15);
        }

        .stat-icon {
          font-size: 32px;
          color: white;
          margin-bottom: 1rem;
        }

        .stat-content .stat-value {
          font-size: 32px;
          font-weight: 800;
          color: white;
          margin-bottom: 0.25rem;
        }

        .stat-content .stat-label {
          font-size: 14px;
          color: rgba(255,255,255,0.9);
        }

        /* Values Section */
        .values-section {
          padding: 6rem 0;
          background: #f8fafc;
        }

        .values-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 2rem;
        }

        .value-card {
          background: white;
          padding: 3rem 2rem;
          border-radius: 20px;
          text-align: center;
          box-shadow: 0 10px 30px rgba(0,0,0,0.05);
          transition: all 0.3s;
          position: relative;
          overflow: hidden;
        }

        .value-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 4px;
          background: linear-gradient(90deg, #FF6B35, #667eea);
        }

        .value-card:hover {
          transform: translateY(-10px);
          box-shadow: 0 30px 40px rgba(0,0,0,0.1);
        }

        .value-icon {
          width: 80px;
          height: 80px;
          background: linear-gradient(135deg, #FF6B35, #667eea);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 2rem;
          font-size: 32px;
          color: white;
          transition: all 0.3s;
        }

        .value-card:hover .value-icon {
          transform: rotateY(180deg);
        }

        .value-card h3 {
          font-size: 22px;
          color: #1a202c;
          margin-bottom: 1rem;
        }

        .value-description {
          color: #718096;
          line-height: 1.8;
          margin-bottom: 1.5rem;
        }

        .value-stats {
          display: inline-block;
          padding: 0.5rem 1.5rem;
          background: #f0f9ff;
          color: #0369a1;
          border-radius: 50px;
          font-size: 14px;
          font-weight: 600;
        }

        /* Timeline */
        .timeline-section {
          padding: 6rem 0;
          background: white;
        }

        .timeline {
          position: relative;
          max-width: 1000px;
          margin: 0 auto;
        }

        .timeline::before {
          content: '';
          position: absolute;
          left: 50%;
          transform: translateX(-50%);
          width: 4px;
          height: 100%;
          background: linear-gradient(135deg, #FF6B35, #667eea);
        }

        .timeline-item {
          position: relative;
          margin-bottom: 3rem;
          width: 50%;
        }

        .timeline-item.left {
          left: 0;
          padding-right: 3rem;
        }

        .timeline-item.right {
          left: 50%;
          padding-left: 3rem;
        }

        .timeline-marker {
          position: absolute;
          top: 20px;
          width: 50px;
          height: 50px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: 20px;
          box-shadow: 0 5px 15px rgba(0,0,0,0.2);
          z-index: 2;
        }

        .timeline-item.left .timeline-marker { right: -25px; }
        .timeline-item.right .timeline-marker { left: -25px; }

        .timeline-content {
          background: #f8fafc;
          padding: 2rem;
          border-radius: 15px;
          box-shadow: 0 5px 15px rgba(0,0,0,0.05);
          position: relative;
        }

        .timeline-content::before {
          content: '';
          position: absolute;
          top: 20px;
          width: 20px;
          height: 20px;
          background: #f8fafc;
          transform: rotate(45deg);
        }

        .timeline-item.left .timeline-content::before { right: -10px; }
        .timeline-item.right .timeline-content::before { left: -10px; }

        .timeline-year {
          display: inline-block;
          background: linear-gradient(135deg, #FF6B35, #667eea);
          color: white;
          padding: 0.25rem 1rem;
          border-radius: 50px;
          font-size: 14px;
          font-weight: 600;
          margin-bottom: 1rem;
        }

        .timeline-content h3 {
          font-size: 20px;
          color: #1a202c;
          margin-bottom: 0.5rem;
        }

        .timeline-content p {
          color: #718096;
          line-height: 1.6;
          margin: 0;
        }

        /* Team Section */
        .team-section {
          padding: 6rem 0;
          background: #f8fafc;
        }

        .team-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 2rem;
        }

        .team-card {
          background: white;
          border-radius: 20px;
          overflow: hidden;
          box-shadow: 0 10px 30px rgba(0,0,0,0.05);
          transition: all 0.3s;
        }

        .team-card:hover {
          transform: translateY(-10px);
          box-shadow: 0 30px 40px rgba(255,107,53,0.1);
        }

        .member-image {
          position: relative;
          overflow: hidden;
          aspect-ratio: 1;
        }

        .member-image img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform 0.5s;
        }

        .team-card:hover .member-image img {
          transform: scale(1.1);
        }

        .member-social {
          position: absolute;
          bottom: -50px;
          left: 0;
          right: 0;
          display: flex;
          justify-content: center;
          gap: 1rem;
          padding: 1rem;
          background: linear-gradient(to top, rgba(0,0,0,0.8), transparent);
          transition: bottom 0.3s;
        }

        .team-card:hover .member-social {
          bottom: 0;
        }

        .social-link {
          width: 40px;
          height: 40px;
          background: white;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #FF6B35;
          text-decoration: none;
          transition: all 0.3s;
        }

        .social-link:hover {
          background: #FF6B35;
          color: white;
          transform: translateY(-3px);
        }

        .member-info {
          padding: 2rem;
        }

        .member-info h3 {
          font-size: 20px;
          color: #1a202c;
          margin-bottom: 0.5rem;
        }

        .member-position {
          color: #FF6B35;
          font-weight: 600;
          margin-bottom: 0.5rem;
        }

        .member-qualification {
          color: #718096;
          font-size: 13px;
          margin-bottom: 1rem;
          padding-bottom: 1rem;
          border-bottom: 1px solid #e2e8f0;
        }

        .member-description {
          color: #4a5568;
          font-size: 14px;
          line-height: 1.6;
          margin-bottom: 1rem;
        }

        .member-expertise {
          display: flex;
          flex-wrap: wrap;
          gap: 0.5rem;
        }

        .expertise-tag {
          background: #f0f9ff;
          color: #0369a1;
          padding: 0.25rem 0.75rem;
          border-radius: 50px;
          font-size: 11px;
          font-weight: 600;
        }

        /* Testimonials */
        .testimonials-section {
          padding: 6rem 0;
          background: white;
        }

        .testimonials-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 2rem;
        }

        .testimonial-card {
          background: #f8fafc;
          padding: 2rem;
          border-radius: 20px;
          position: relative;
          transition: all 0.3s;
        }

        .testimonial-card:hover {
          transform: translateY(-10px);
          box-shadow: 0 20px 30px rgba(255,107,53,0.1);
        }

        .quote-icon {
          position: absolute;
          top: 1rem;
          right: 1rem;
          font-size: 48px;
          color: #FF6B35;
          opacity: 0.1;
        }

        .testimonial-text {
          color: #4a5568;
          line-height: 1.8;
          margin-bottom: 2rem;
          font-style: italic;
        }

        .testimonial-footer {
          display: flex;
          align-items: center;
          gap: 1rem;
          margin-bottom: 1rem;
        }

        .testimonial-footer img {
          width: 60px;
          height: 60px;
          border-radius: 50%;
          object-fit: cover;
        }

        .testimonial-footer h4 {
          font-size: 18px;
          color: #1a202c;
          margin-bottom: 0.25rem;
        }

        .testimonial-footer p {
          color: #718096;
          font-size: 14px;
        }

        .testimonial-rating {
          color: #ffc107;
        }

        /* CTA Section */
        .cta-section {
          padding: 5rem 0;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          text-align: center;
        }

        .cta-content h2 {
          font-size: 42px;
          margin-bottom: 1rem;
          font-weight: 800;
        }

        .cta-content p {
          font-size: 20px;
          margin-bottom: 2rem;
          opacity: 0.95;
        }

        .cta-buttons {
          display: flex;
          gap: 1rem;
          justify-content: center;
        }

        /* Responsive */
        @media (max-width: 1200px) {
          .story-grid { grid-template-columns: repeat(2, 1fr); }
          .values-grid { grid-template-columns: repeat(2, 1fr); }
          .team-grid { grid-template-columns: repeat(2, 1fr); }
          .testimonials-grid { grid-template-columns: repeat(2, 1fr); }
          .stats-grid { grid-template-columns: repeat(3, 1fr); }
        }

        @media (max-width: 992px) {
          .timeline::before { left: 20px; }
          .timeline-item {
            width: 100%;
            padding-left: 50px !important;
            padding-right: 0 !important;
            left: 0 !important;
          }
          .timeline-item.left .timeline-marker,
          .timeline-item.right .timeline-marker { left: -5px !important; }
        }

        @media (max-width: 768px) {
          .hero-title .title-line { font-size: 48px; }
          .hero-stats { flex-direction: column; gap: 2rem; }
          .story-grid,
          .values-grid,
          .team-grid,
          .testimonials-grid { grid-template-columns: 1fr; }
          .stats-grid { grid-template-columns: repeat(2, 1fr); }
          .cta-buttons { flex-direction: column; }
          .cta-btn { width: 100%; max-width: 300px; margin: 0 auto; }
        }

        @media (max-width: 480px) {
          .container { padding: 0 1rem; }
          .stats-grid { grid-template-columns: 1fr; }
          .hero-title .title-line { font-size: 36px; }
        }
      `}</style>
    </div>
  );
};

export default About;
